import React, { MouseEventHandler, ReactElement, ReactNode } from "react"
import { Alert as MaterialAlert } from "@mui/material"
import { Check, Close, ErrorOutline, InfoOutlined } from "@mui/icons-material"
import { ButtonText } from "../../atoms/button"
import styled from "styled-components"
import { theme } from "../../../theme"
import { WarningIcon } from "../../../icons"

export type InfoBarAction = {
  label: string
  onClick: MouseEventHandler<HTMLButtonElement>
}

export enum InfoBarVariant {
  Info = "info",
  Warning = "warning",
  Error = "error",
}

export type InfoBarProps = {
  variant?: InfoBarVariant
  icon?: ReactElement
  children?: ReactNode
  actions?: InfoBarAction[]
  onClose?: () => void
}

const getBackgroundColor = (variant?: InfoBarVariant) => {
  switch (variant) {
    case InfoBarVariant.Error:
      return theme.color.error
    case InfoBarVariant.Warning:
      return theme.color.warning
    case InfoBarVariant.Info:
      return theme.color.primary50
    default:
      return "transparent"
  }
}

const getIcon = (variant?: InfoBarVariant) => {
  switch (variant) {
    case InfoBarVariant.Error:
      return <ErrorOutline />
    case InfoBarVariant.Warning:
      return <WarningIcon />
    case InfoBarVariant.Info:
      return <InfoOutlined />
    default:
      return <Check />
  }
}

export const InfoBar = (props: InfoBarProps) => {
  const { variant = InfoBarVariant.Info, icon, children, actions = [], onClose } = props

  return (
    <Alert
      $variant={variant}
      severity={variant}
      icon={icon ?? getIcon(variant)}
      action={
        <>
          {actions.map((action, index) => (
            <ActionButton key={`${action.label}-${index}`} onClick={action.onClick}>
              {action.label}
            </ActionButton>
          ))}
          {!!onClose && (
            <CloseButton onClick={onClose}>
              <Close />
            </CloseButton>
          )}
        </>
      }
      onClose={onClose}
    >
      {children}
    </Alert>
  )
}

const getTextColor = (variant?: InfoBarVariant) =>
  variant === InfoBarVariant.Info ? theme.color.primary : theme.color.white

const ActionButton = styled(ButtonText)`
  color: ${theme.color.primary};
  padding: ${theme.spacing.m} ${theme.spacing.l};

  &:hover {
    background-color: transparent;
  }
`

const CloseButton = styled(ButtonText)`
  min-width: ${theme.buttonSize.m};
  color: ${theme.color.primary};
  padding: ${theme.spacing.m};

  &:hover {
    background-color: transparent;
  }
`

const Alert = styled(MaterialAlert)<{ $variant: InfoBarVariant }>`
  display: flex;
  font-size: ${theme.fontSize.m};
  padding: ${theme.spacing.m};
  padding-left: ${theme.spacing.l};
  background-color: ${(p) => getBackgroundColor(p.$variant)};
  color: ${(p) => getTextColor(p.$variant)};
  border-radius: 0;

  & .MuiAlert-icon {
    color: ${theme.color.primary};
    margin-right: ${theme.spacing.m};
  }

  & .MuiAlert-message {
    margin-top: auto;
    margin-bottom: auto;
  }

  & .MuiAlert-action {
    margin-right: 0;
    align-items: flex-start;
  }

  svg {
    color: ${(p) => getTextColor(p.$variant)};
  }

  ${ActionButton} {
    color: ${(p) => getTextColor(p.$variant)};
  }
`
